import { LitElement, html, css, nothing } from 'lit';
import { customElement, property, query } from 'lit/decorators.js';
import '../components/cos-icon.js';
import '../components/cos-button.js';
import { ViewportController } from './viewport-controller.js';

/**
 * <cos-modal> — THE dialog primitive.
 *
 * WHY THIS EXISTS: 60+ files in this codebase hand-roll their own
 * `role="dialog"` overlay — each a slightly different backdrop, a
 * slightly different Escape/backdrop-click wiring, a slightly different
 * focus trap (or none). `cos-record-modal-base` and `cos-side-panel` each
 * independently arrived at the same shape (native `<dialog>` for real
 * top-layer promotion + a manual focus trap as a safety net for
 * environments where `showModal()` degrades). This primitive is that
 * shape, generalized, so a NEW overlay composes it instead of
 * reinventing it a 64th time.
 *
 * Native `<dialog>` + `showModal()` matters for one concrete reason: a
 * plain `position: fixed` element is contained by any ancestor that
 * establishes a containing block for fixed descendants (e.g.
 * `cos-record-shell`'s `container-type: inline-size`) — a modal launched
 * from inside such an ancestor gets trapped in its box and can paint
 * behind page content. The browser's top layer escapes every such trap.
 * `showModal()`/`.close()` are wrapped in try/catch because environments
 * without real `<dialog>` support (the vitest/happy-dom test environment)
 * throw — the component still renders and behaves correctly via its own
 * JS-driven open/close, focus, and trap logic; only the native top-layer
 * promotion is unavailable there.
 *
 * CONTROLLED, not self-closing: the consumer owns `open`. Backdrop click,
 * Escape, and the close button all just dispatch `cos:close` with a
 * `reason` — the modal itself never flips `open`. This mirrors
 * `cos-side-panel`'s contract and is what makes an "are you sure you want
 * to discard this?" second-modal-on-top-of-a-modal safe to build: nothing
 * here assumes it's the only thing deciding when it goes away.
 *
 * Props:
 *   - open: boolean (controlled)
 *   - heading: string — becomes the dialog's accessible name via
 *     aria-labelledby
 *   - size: 'sm' | 'md' | 'lg' (default 'md') — panel max-width
 *   - closeOnBackdrop: boolean (default true)
 *   - closeOnEsc: boolean (default true) — Escape is still SWALLOWED
 *     (preventDefault + stopPropagation) even when this is false, so a
 *     mandatory-action modal doesn't leak Escape to a page shortcut
 *     behind it; it just doesn't request a close.
 *   - hideClose: boolean (default false) — hides the header × button.
 *     Only meaningful alongside a footer Cancel action or
 *     closeOnBackdrop/closeOnEsc — don't set it on a modal with no other
 *     way out.
 *
 * THE STANDARD FOOTER (the composition contract — see
 * docs/design-system/component-vocabulary.md "Composition patterns"):
 * set `primaryLabel` and cos-modal renders the canonical
 * `[tertiary?] … [Cancel] [Primary]` footer ITSELF — right-aligned
 * cancel+primary pair, an optional tertiary action left-aligned in the
 * same row. **When `primaryLabel` is set, the `footer` slot is IGNORED**
 * (a modal is either in standard-footer mode or slot mode, never both —
 * mixing them would leave a stray unused slot no consumer meant to fill).
 * Leave `primaryLabel` unset (the default, `''`) to keep composing your
 * own footer via the slot, byte-identical to before this existed.
 *
 *   - primaryLabel: string (default '') — set this to opt into the
 *     standard footer.
 *   - primaryVariant: 'primary' | 'danger' (default 'primary') — use
 *     'danger' for a destructive primary action; pair it with an
 *     explicit verb label ("Delete", never "OK").
 *   - primaryDisabled: boolean (default false)
 *   - primaryLoading: boolean (default false) — puts the primary button
 *     in its loading state AND, while true, blocks the Cancel button,
 *     Escape, and a backdrop click from requesting a close (a save in
 *     flight must not be abandoned accidentally). The header × button
 *     is deliberately NOT blocked — it stays as an explicit way out if a
 *     save gets stuck.
 *   - cancelLabel: string (default 'Cancel')
 *   - tertiaryLabel: string (default '') — a rare third action (e.g.
 *     "Reset"), rendered left-aligned, visually separated from the
 *     cancel/primary pair.
 *
 * Slots:
 *   - default — the body. An element with `autofocus` here takes
 *     priority over the standard footer's default primary-button focus.
 *   - "footer" — the action row, used only when `primaryLabel` is unset.
 *     Rendered only when the consumer has actually put something in it
 *     (checked via a light-DOM query at render time, so an unused footer
 *     slot doesn't leave a stray empty bar with padding).
 *
 * Events:
 *   - `cos:close` — detail `{ reason: 'backdrop' | 'esc' | 'button' | 'cancel' }`.
 *     'cancel' is the standard footer's Cancel button; the modal never
 *     closes itself for any reason — the consumer always does.
 *   - `cos:primary` — the standard footer's primary button was clicked.
 *     Does NOT close the modal — the consumer runs its action and closes
 *     on success, same controlled contract as everything else here.
 *   - `cos:tertiary` — the standard footer's tertiary button was clicked
 *     (only fires when `tertiaryLabel` is set).
 *
 * COMPACT MODE (Workstream A1, spec §6): on a `compact`-tier viewport (via
 * the shared `ViewportController` — never a component-local `matchMedia`),
 * the panel becomes a full-width bottom sheet instead of a centered dialog:
 * anchored to the bottom edge, rounded top corners only, safe-area-aware
 * bottom padding (so a home-indicator device doesn't clip the footer), and
 * scroll-locked exactly as before. v1 is drag-free — the header × button,
 * backdrop tap, and Esc are still the only ways out; a drag-to-dismiss
 * gesture is a later increment, not a blocker for this shape. Every other
 * behavior (focus trap, controlled `open`, standard footer, events) is
 * byte-identical between the two presentations.
 *
 * Focus trap: this component's own `_focusables()` deliberately mirrors
 * `cos-side-panel`'s — native-selector elements plus one level into
 * slotted nodes' light-DOM descendants — and deliberately does NOT drill
 * into a slotted custom element's own shadow root (e.g. a `<cos-button>`
 * in the footer). That's not a testing shortcut: in a real browser,
 * `<dialog>` + `showModal()` already constrains Tab natively regardless
 * of what's inside, so this JS trap only has to matter in a degraded
 * environment, and keeping it to a flat native-selector query keeps it
 * deterministic to unit-test. `cos-side-panel` additionally filters by
 * `offsetParent`, which happy-dom never populates with real layout — this
 * primitive skips that filter so its own trap tests are meaningful rather
 * than accidentally vacuous.
 */

export type CosModalCloseReason = 'backdrop' | 'esc' | 'button' | 'cancel';

// Exported so any other full-viewport overlay (cos-lightbox is the first
// consumer) shares this SAME counter rather than rolling its own — two
// independent counters would let closing one overlay clear body scroll out
// from under another that's still open (e.g. a lightbox launched from
// inside a modal).
let _lockCount = 0;
export function lockScroll(): void {
	if (_lockCount === 0) document.body.style.overflow = 'hidden';
	_lockCount++;
}
export function unlockScroll(): void {
	_lockCount = Math.max(0, _lockCount - 1);
	if (_lockCount === 0) document.body.style.overflow = '';
}

let _headingSeq = 0;

const FOCUSABLE_SELECTOR =
	'a[href], button:not([disabled]), input:not([disabled]), textarea:not([disabled]), select:not([disabled]), [tabindex]:not([tabindex="-1"])';

@customElement('cos-modal')
export class CosModal extends LitElement {
	@property({ type: Boolean, reflect: true }) open = false;
	@property({ type: String }) heading = '';
	@property({ type: String }) size: 'sm' | 'md' | 'lg' = 'md';
	@property({ type: Boolean, attribute: 'close-on-backdrop' }) closeOnBackdrop = true;
	@property({ type: Boolean, attribute: 'close-on-esc' }) closeOnEsc = true;
	@property({ type: Boolean, attribute: 'hide-close' }) hideClose = false;

	// The standard footer — see the class doc comment.
	@property({ type: String, attribute: 'primary-label' }) primaryLabel = '';
	@property({ type: String, attribute: 'primary-variant' }) primaryVariant: 'primary' | 'danger' = 'primary';
	// 'top' keeps the panel's top edge still instead of centring it vertically.
	@property({ type: String, reflect: true }) anchor: 'center' | 'top' = 'center';
	@property({ type: Boolean, attribute: 'primary-disabled' }) primaryDisabled = false;
	@property({ type: Boolean, attribute: 'primary-loading' }) primaryLoading = false;
	@property({ type: String, attribute: 'cancel-label' }) cancelLabel = 'Cancel';
	@property({ type: String, attribute: 'tertiary-label' }) tertiaryLabel = '';

	@query('dialog') private _dialogEl?: HTMLDialogElement;
	@query('.panel') private _panelEl?: HTMLElement;

	// Workstream A1 — drives the bottom-sheet presentation below. Shared
	// listeners across every mounted modal (see the class doc comment);
	// this is NOT a component-local matchMedia call.
	private _viewport = new ViewportController(this);

	private readonly _headingId = `cos-modal-heading-${++_headingSeq}`;
	private _invoker: Element | null = null;
	private _locked = false;

	static styles = css`
		:host {
			display: contents;
		}
		dialog.backdrop {
			position: fixed;
			inset: 0;
			width: 100%;
			height: 100%;
			max-width: 100vw;
			max-height: 100vh;
			margin: 0;
			border: 0;
			padding: 0;
			background: color-mix(in srgb, black 40%, transparent);
			z-index: var(--cos-z-modal, 50);
		}
		/* Painted on the dialog itself; the native ::backdrop stays
		   transparent so there's no double-dim. */
		dialog.backdrop::backdrop {
			background: transparent;
		}
		dialog.backdrop[open] {
			display: flex;
			align-items: center;
			justify-content: center;
		}
		/* Workstream A1 compact mode — the sheet docks to the bottom edge
		   instead of centering; .panel.compact (below) sets its own width
		   to 100%, so centering it on the still-active main axis is a
		   no-op (nothing to center against once it fills the row). */
		dialog.backdrop[open].compact {
			align-items: flex-end;
		}
		/* anchor="top": the panel's top edge stays put while its height changes
		   (a dialog whose content comes and goes, such as search results, would
		   otherwise jump up and down as the centred panel grows and shrinks). */
		dialog.backdrop[open].anchor-top {
			align-items: flex-start;
			padding-top: 12vh;
			box-sizing: border-box;
		}
		dialog.backdrop.anchor-top .panel {
			max-height: calc(88vh - 2rem);
		}
		/* Closed-and-unrendered dialog still needs display:none — a
		   native dialog with no [open] attribute defaults to
		   display:none anyway, but we set it explicitly since we never
		   toggle the HTML [open] attribute ourselves (showModal()/close()
		   own that). */
		dialog.backdrop:not([open]) {
			display: none;
		}
		.panel {
			display: flex;
			flex-direction: column;
			gap: var(--cos-space-4);
			width: min(var(--modal-width, 560px), calc(100vw - 2rem));
			max-height: calc(100vh - 4rem);
			overflow-y: auto;
			box-sizing: border-box;
			padding: var(--cos-space-5);
			background: var(--cos-surface);
			border-radius: var(--cos-radius-lg);
			box-shadow: var(--cos-shadow-floating);
			outline: none;
		}
		.panel.size-sm {
			--modal-width: 400px;
		}
		.panel.size-md {
			--modal-width: 560px;
		}
		.panel.size-lg {
			--modal-width: 800px;
		}
		/* Workstream A1 — bottom sheet. Overrides the size-driven width: a
		   sheet is always full-width regardless of size. Rounded top
		   corners only (flush against the bottom edge); safe-area-aware
		   bottom padding on the FRAME (not the inner body/footer, which
		   keep their normal --cos-space-5 gap from the frame edge) so a
		   home-indicator device doesn't clip the footer's action row. */
		.panel.compact {
			width: 100%;
			max-width: 100%;
			/* vh first: browsers without dvh support drop the dvh line and
			   keep this one (the cos-chat/cos-mail-inbox precedent). */
			max-height: 85vh;
			max-height: 85dvh;
			border-radius: var(--cos-radius-lg) var(--cos-radius-lg) 0 0;
			padding-bottom: calc(var(--cos-space-5) + env(safe-area-inset-bottom, 0px));
		}
		.head {
			display: flex;
			align-items: flex-start;
			justify-content: space-between;
			gap: var(--cos-space-3);
		}
		.head h2 {
			margin: 0;
			flex: 1;
			min-width: 0;
			font-size: var(--cos-font-size-lg);
			font-weight: var(--cos-font-weight-semibold);
			color: var(--cos-text-primary);
		}
		.close {
			flex: none;
			display: inline-flex;
			border: none;
			background: transparent;
			cursor: pointer;
			color: var(--cos-text-secondary);
			padding: var(--cos-space-1);
			border-radius: var(--cos-radius-sm);
			margin: calc(-1 * var(--cos-space-1)) calc(-1 * var(--cos-space-1)) 0 0;
		}
		.close:hover {
			background: var(--cos-surface-raised);
		}
		.close:focus-visible {
			outline: none;
			box-shadow: var(--cos-shadow-focus);
		}
		.body {
			min-height: 0;
			overflow-y: auto;
			color: var(--cos-text-primary);
			font-size: var(--cos-font-size-sm);
		}
		.footer {
			display: flex;
			flex-wrap: wrap;
			align-items: center;
			justify-content: flex-end;
			gap: var(--cos-space-2);
		}
		/* Standard footer with a tertiary action: tertiary left-aligned,
		   cancel+primary right-aligned, in the same row. */
		.footer.with-tertiary {
			justify-content: space-between;
		}
		/* A long primary label on a narrow screen wraps the actions onto
		   their own row instead of running off the dialog edge. */
		.footer-actions {
			display: flex;
			flex-wrap: wrap;
			justify-content: flex-end;
			gap: var(--cos-space-2);
		}
	`;

	updated(changed: Map<string, unknown>): void {
		super.updated(changed);
		if (changed.has('open')) {
			if (this.open) this._onOpen();
			else this._onClose();
		}
	}

	disconnectedCallback(): void {
		super.disconnectedCallback();
		// Safety net: a modal removed from the DOM while still open (e.g. its
		// page navigated away) must not leave body scroll permanently locked.
		if (this._locked) {
			unlockScroll();
			this._locked = false;
		}
	}

	private _onOpen(): void {
		this._invoker = document.activeElement;
		if (!this._locked) {
			lockScroll();
			this._locked = true;
		}
		const dlg = this._dialogEl;
		if (dlg && !dlg.open && typeof dlg.showModal === 'function') {
			try {
				dlg.showModal();
			} catch {
				/* unsupported env (e.g. vitest/happy-dom) — content still
				   renders + behaves via our own JS, minus native top-layer. */
			}
		}
		void this.updateComplete.then(() => {
			void this._landFocus();
		});
	}

	// An element with [autofocus] in the body slot always wins; otherwise
	// the standard footer's primary button takes focus (a JS-driven
	// .focus() call, so — unlike the Tab trap below — it DOES need to
	// reach into cos-button's own shadow root: a bare <cos-button> host
	// has no tabindex/delegatesFocus, so .focus() on the host itself is a
	// no-op). Falls back to the panel wrapper, matching prior behavior.
	private async _landFocus(): Promise<void> {
		const autofocusEl = this._autofocusTarget();
		if (autofocusEl) {
			autofocusEl.focus();
			return;
		}
		if (this._standardFooter) {
			const primaryHost = this.renderRoot.querySelector('cos-button.primary') as
				| (HTMLElement & { updateComplete?: Promise<boolean> })
				| null;
			if (primaryHost) {
				await primaryHost.updateComplete;
				const inner = primaryHost.shadowRoot?.querySelector('button') as HTMLElement | null;
				if (inner) {
					inner.focus();
					return;
				}
			}
		}
		this._panelEl?.focus();
	}

	private _autofocusTarget(): HTMLElement | null {
		const bodySlot = this.renderRoot.querySelector<HTMLSlotElement>('slot:not([name])');
		for (const node of bodySlot?.assignedElements({ flatten: true }) ?? []) {
			if (!(node instanceof HTMLElement)) continue;
			if (node.hasAttribute('autofocus')) return node;
			const nested = node.querySelector<HTMLElement>('[autofocus]');
			if (nested) return nested;
		}
		return null;
	}

	private _onClose(): void {
		if (this._locked) {
			unlockScroll();
			this._locked = false;
		}
		const dlg = this._dialogEl;
		if (dlg?.open) {
			try {
				dlg.close();
			} catch {
				/* no-op */
			}
		}
		const el = this._invoker as HTMLElement | null;
		this._invoker = null;
		if (el && typeof el.focus === 'function') el.focus();
	}

	private _requestClose(reason: CosModalCloseReason): void {
		// A save in flight must not be abandoned accidentally — see the
		// primaryLoading doc comment. The header × button (reason
		// 'button') is deliberately exempt: it stays as an explicit way
		// out if a save gets stuck.
		if (this.primaryLoading && reason !== 'button') return;
		this.dispatchEvent(
			new CustomEvent('cos:close', { detail: { reason }, bubbles: true, composed: true }),
		);
	}

	private _onBackdropClick(e: MouseEvent): void {
		if (!this.closeOnBackdrop) return;
		if (e.target === e.currentTarget) this._requestClose('backdrop');
	}

	private _onPrimaryClick(): void {
		this.dispatchEvent(new CustomEvent('cos:primary', { bubbles: true, composed: true }));
	}

	private _onTertiaryClick(): void {
		this.dispatchEvent(new CustomEvent('cos:tertiary', { bubbles: true, composed: true }));
	}

	private get _standardFooter(): boolean {
		return !!this.primaryLabel;
	}

	// See the class doc comment for why this deliberately doesn't drill
	// into a slotted custom element's own shadow root (e.g. a <cos-button>
	// in the footer) — a real <dialog> + showModal() already traps Tab
	// correctly regardless of what's inside, so this JS fallback only has
	// to be roughly right for degraded (non-native-dialog) environments.
	// The standard footer's cos-button hosts get the SAME treatment for
	// consistency, even though they're host-rendered rather than slotted —
	// landing INITIAL focus (_landFocus above) is the one place that
	// actually needs to drill in, because that's a one-shot imperative
	// .focus() call rather than a Tab-key trap.
	private _focusables(): HTMLElement[] {
		const out: HTMLElement[] = [];
		const closeBtn = this.renderRoot.querySelector<HTMLElement>('.close');
		if (closeBtn) out.push(closeBtn);
		const bodySlot = this.renderRoot.querySelector<HTMLSlotElement>('slot:not([name])');
		for (const node of bodySlot?.assignedElements({ flatten: true }) ?? []) {
			if (node instanceof HTMLElement && node.matches(FOCUSABLE_SELECTOR)) out.push(node);
			out.push(...Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)));
		}
		if (this._standardFooter) {
			for (const sel of ['.tertiary', '.cancel', '.primary']) {
				const hostEl = this.renderRoot.querySelector<HTMLElement & { disabled?: boolean }>(sel);
				if (hostEl && !hostEl.disabled) out.push(hostEl);
			}
		} else {
			const footerSlot = this.renderRoot.querySelector<HTMLSlotElement>('slot[name="footer"]');
			for (const node of footerSlot?.assignedElements({ flatten: true }) ?? []) {
				if (node instanceof HTMLElement && node.matches(FOCUSABLE_SELECTOR)) out.push(node);
				out.push(...Array.from(node.querySelectorAll<HTMLElement>(FOCUSABLE_SELECTOR)));
			}
		}
		return out;
	}

	private _onKeydown(e: KeyboardEvent): void {
		if (e.key === 'Escape') {
			// Own Escape unconditionally (never let it leak to a page/global
			// shortcut behind an open modal) — the Stage 81 cross-shadow
			// keyboard leak class. Whether it actually CLOSES is separate.
			e.preventDefault();
			e.stopPropagation();
			if (this.closeOnEsc) this._requestClose('esc');
			return;
		}
		if (e.key === 'Tab') this._trapTab(e);
	}

	private _trapTab(e: KeyboardEvent): void {
		const f = this._focusables();
		if (f.length === 0) {
			e.preventDefault();
			return;
		}
		const active = (this.shadowRoot?.activeElement as HTMLElement | null) ?? document.activeElement;
		const first = f[0];
		const last = f[f.length - 1];
		if (e.shiftKey) {
			if (active === first || active === this._panelEl) {
				e.preventDefault();
				last.focus();
			}
		} else if (active === last) {
			e.preventDefault();
			first.focus();
		}
	}

	// Computed at render time from the light DOM (not reactive to a later
	// dynamic slot change beyond the slotchange listener below) — covers
	// the overwhelmingly common case of a footer declared up front. Only
	// consulted when NOT in standard-footer mode (primaryLabel unset) —
	// the two footer modes are mutually exclusive; see the class doc
	// comment.
	private get _hasSlotFooter(): boolean {
		return this.querySelector('[slot="footer"]') !== null;
	}

	private get _compact(): boolean {
		return this._viewport.tier === 'compact';
	}

	render() {
		return html`
			<dialog
				class="backdrop${this._compact ? ' compact' : ''}${!this._compact && this.anchor === 'top' ? ' anchor-top' : ''}"
				@click=${this._onBackdropClick}
				@cancel=${(e: Event) => e.preventDefault()}
			>
				${this.open ? this._renderPanel() : nothing}
			</dialog>
		`;
	}

	private _renderPanel() {
		return html`
			<div
				class="panel size-${this.size}${this._compact ? ' compact' : ''}"
				role="dialog"
				aria-modal="true"
				aria-labelledby=${this._headingId}
				tabindex="-1"
				@keydown=${this._onKeydown}
				@click=${(e: Event) => e.stopPropagation()}
			>
				<header class="head">
					<h2 id=${this._headingId}>${this.heading}</h2>
					${this.hideClose
						? nothing
						: html`
								<button type="button" class="close" aria-label="Close" @click=${() => this._requestClose('button')}>
									<cos-icon name="x" size="16"></cos-icon>
								</button>
							`}
				</header>
				<div class="body"><slot></slot></div>
				${this._standardFooter
					? this._renderStandardFooter()
					: this._hasSlotFooter
						? html`<div class="footer"><slot name="footer" @slotchange=${() => this.requestUpdate()}></slot></div>`
						: nothing}
			</div>
		`;
	}

	private _renderStandardFooter() {
		return html`
			<div class="footer${this.tertiaryLabel ? ' with-tertiary' : ''}">
				${this.tertiaryLabel
					? html`
							<cos-button
								class="tertiary"
								variant="ghost"
								?disabled=${this.primaryLoading}
								@click=${this._onTertiaryClick}
							>
								${this.tertiaryLabel}
							</cos-button>
						`
					: nothing}
				<div class="footer-actions">
					<cos-button
						class="cancel"
						variant="secondary"
						?disabled=${this.primaryLoading}
						@click=${() => this._requestClose('cancel')}
					>
						${this.cancelLabel}
					</cos-button>
					<cos-button
						class="primary"
						variant=${this.primaryVariant}
						?loading=${this.primaryLoading}
						?disabled=${this.primaryDisabled || this.primaryLoading}
						@click=${this._onPrimaryClick}
					>
						${this.primaryLabel}
					</cos-button>
				</div>
			</div>
		`;
	}
}

declare global {
	interface HTMLElementTagNameMap {
		'cos-modal': CosModal;
	}
}
